/**
 * WebXRScanner — Android Chrome ARCore Depth 量房骨架。
 * 流程：
 * 1. 進入 immersive-ar session、要 hit-test feature
 * 2. user 點螢幕中央 reticle 在地板上標角點
 * 3. 連續標 ≥3 點、按「完成」自動閉合
 * 4. 角點轉成米座標、產 FreePolygon → 傳出
 *
 * 精度：ARCore hit-test 在工地光線下 ±5-8cm。
 */

import { FreePolygon, Point2D } from '../lib/polygon';

export interface ScanResult {
  polygon: FreePolygon;
  rawPointsM: { x: number; y: number; z: number }[];
}

export class WebXRScanner {
  private session: XRSession | null = null;
  private refSpace: XRReferenceSpace | null = null;
  private hitTestSource: XRHitTestSource | null = null;
  private gl: WebGLRenderingContext | null = null;
  private canvas: HTMLCanvasElement;
  private overlay: HTMLDivElement;
  private points: Point2D[] = [];   // 投影到水平面 (x,z) 當 polygon (x,y)
  private rawPoints: { x: number; y: number; z: number }[] = [];
  private lastReticleHit: { x: number; y: number; z: number } | null = null;

  constructor(private opts: {
    onFinish: (r: ScanResult) => void;
    onCancel: () => void;
  }) {
    this.canvas = document.createElement('canvas');
    this.canvas.style.position = 'fixed';
    this.canvas.style.inset = '0';
    this.canvas.style.width = '100%';
    this.canvas.style.height = '100%';

    this.overlay = document.createElement('div');
    this.overlay.id = 'xr-overlay';
    this.overlay.style.cssText = `
      position:fixed;inset:0;
      display:flex;flex-direction:column;justify-content:space-between;
      padding:24px;color:white;
      font-family:-apple-system,sans-serif;
      pointer-events:none;
    `;
    this.overlay.innerHTML = `
      <div style="background:rgba(0,0,0,0.6);padding:12px;border-radius:8px;">
        <div id="hint" style="font-size:14px;">將紅圈對準牆角 → 按下方「+ 加角點」</div>
        <div id="count" style="font-size:12px;margin-top:4px;opacity:.8;">0 個角點</div>
      </div>
      <div style="display:flex;justify-content:center;align-items:center;flex:1;">
        <div id="reticle" style="
          width:48px;height:48px;border:3px solid #ef4444;border-radius:50%;
          display:flex;align-items:center;justify-content:center;
        "><div style="width:8px;height:8px;background:#ef4444;border-radius:50%;"></div></div>
      </div>
      <div style="display:flex;gap:12px;pointer-events:auto;">
        <button id="cancel" style="
          flex:1;padding:16px;border-radius:12px;border:none;
          background:rgba(255,255,255,0.2);color:white;font-size:16px;
        ">取消</button>
        <button id="add" style="
          flex:2;padding:16px;border-radius:12px;border:none;
          background:#22c55e;color:white;font-size:16px;font-weight:600;
        ">+ 加角點</button>
        <button id="finish" style="
          flex:1;padding:16px;border-radius:12px;border:none;
          background:#3b82f6;color:white;font-size:16px;font-weight:600;
        ">完成</button>
      </div>
    `;
  }

  static async isSupported(): Promise<boolean> {
    if (!('xr' in navigator)) return false;
    try {
      return await (navigator as any).xr.isSessionSupported('immersive-ar');
    } catch { return false; }
  }

  async start() {
    if (!(await WebXRScanner.isSupported())) {
      alert('此裝置不支援 WebXR AR。請用 Android Chrome 88+。');
      this.opts.onCancel();
      return;
    }
    document.body.appendChild(this.canvas);
    document.body.appendChild(this.overlay);
    this.bindButtons();

    this.gl = this.canvas.getContext('webgl', { xrCompatible: true } as any) as WebGLRenderingContext;
    try {
      this.session = await (navigator as any).xr.requestSession('immersive-ar', {
        requiredFeatures: ['hit-test', 'local-floor'],
        optionalFeatures: ['dom-overlay'],
        domOverlay: { root: this.overlay },
      });
    } catch (e: any) {
      alert('WebXR 啟動失敗：' + e.message + '\n\n換條路：點「從矩形開始」手繪也可估料。');
      this.cleanup();
      this.opts.onCancel();
      return;
    }
    const session = this.session!;
    await (this.gl as any).makeXRCompatible();
    session.updateRenderState({
      baseLayer: new (window as any).XRWebGLLayer(session, this.gl),
    });
    this.refSpace = await session.requestReferenceSpace('local-floor');
    const viewerSpace = await session.requestReferenceSpace('viewer');
    this.hitTestSource = await (session as any).requestHitTestSource({ space: viewerSpace });

    session.requestAnimationFrame((t: number, f: XRFrame) => this.onFrame(t, f));
    session.addEventListener('end', () => this.cleanup());
  }

  private bindButtons() {
    this.overlay.querySelector<HTMLButtonElement>('#add')!.onclick = () => this.addPoint();
    this.overlay.querySelector<HTMLButtonElement>('#finish')!.onclick = () => this.finish();
    this.overlay.querySelector<HTMLButtonElement>('#cancel')!.onclick = () => {
      this.session?.end();
      this.opts.onCancel();
    };
  }

  private onFrame(_t: number, frame: XRFrame) {
    const session = frame.session;
    session.requestAnimationFrame((t: number, f: XRFrame) => this.onFrame(t, f));
    if (!this.hitTestSource || !this.refSpace) return;
    const hits = (frame as any).getHitTestResults(this.hitTestSource);
    if (hits.length) {
      const pose = hits[0].getPose(this.refSpace);
      if (pose) {
        this.lastReticleHit = {
          x: pose.transform.position.x,
          y: pose.transform.position.y,
          z: pose.transform.position.z,
        };
      }
    } else {
      this.lastReticleHit = null;
    }
  }

  private addPoint() {
    if (!this.lastReticleHit) {
      this.flashHint('還沒偵測到平面、移動手機讓 ARCore 抓地板');
      return;
    }
    const p = this.lastReticleHit;
    this.rawPoints.push(p);
    this.points.push({ x: p.x, y: p.z });   // 用水平 X-Z 平面當 polygon
    this.overlay.querySelector('#count')!.textContent = `${this.points.length} 個角點`;
    this.flashHint(`加了第 ${this.points.length} 點`);
  }

  private flashHint(s: string) {
    const el = this.overlay.querySelector<HTMLElement>('#hint')!;
    el.textContent = s;
  }

  private finish() {
    if (this.points.length < 3) {
      this.flashHint(`至少要 3 個角點、目前 ${this.points.length}`);
      return;
    }
    const poly = new FreePolygon(this.points);
    this.session?.end();
    this.opts.onFinish({ polygon: poly, rawPointsM: this.rawPoints });
  }

  private cleanup() {
    this.canvas.remove();
    this.overlay.remove();
    this.session = null;
    this.refSpace = null;
    this.hitTestSource = null;
  }
}
